import moment from 'moment'
// jsPDF is pinned at 1.5.3 (default export, setFontType) and jspdf-autotable at
// 3.5.x — the same idiom as ReconciliationV3/auditReport.js.
import jsPDF from 'jspdf'
// __createTable measures a table without drawing it (present in the pinned 3.5.x).
import autoTable, { __createTable } from 'jspdf-autotable'
import { formatAlertTypeLabel } from './utils'
// Official AssetPro wordmark (on-navy variant), shared with the reconciliation report.
import { LOGO_ON_NAVY, LOGO_W, LOGO_H } from '../ReconciliationV3/brandLogo'
// Emoji and characters Helvetica can't print (see util/pdfText.js, AL-00885).
import { createPdfTextTracker, toPdfText, unprintableNote } from '../../util/pdfText'

/**
 * Client-shareable "AIM Update" PDF for a single alert.
 *
 * Contents: alert header (serial no, date, organisation, asset, model, location),
 * the alert text, the analyst's Analysis / Suggested Action / Action Taken, and the
 * image attachments embedded as evidence. Layout follows the sample
 * "AIM_Refuel_Mismatch_Report_AL-00747.pdf".
 *
 * Data reliability — this document leaves the organisation, so:
 *  - Only SAVED alert data is used (never unsaved text typed in the drawer).
 *  - A missing field prints "Not recorded" — never blank, zero or a guessed value.
 *  - An image that cannot be fetched/decoded is NOT silently dropped: the PDF lists
 *    it under "Attachments not embedded" with the reason, and the caller is told.
 *  - Non-image attachments (PDF, Excel…) cannot be embedded; they are listed by name.
 * See docs/alert-pdf-report.md.
 */

// ---- AssetPro Design Foundations, light mode (Branding and Marketing/
// "AssetPro Design Foundations (standalone).html"). Same token names as
// ReconciliationV3/auditReport.js. Soft tints are the foundation rgba "-soft"
// values flattened onto white, because jsPDF 1.5.3 has no alpha fills.
// Typeface: Helvetica stands in for IBM Plex Sans. jsPDF 1.5.3 cannot wrap text
// in a custom TTF, and Helvetica was the agreed choice for now.
const C = {
  navy: [10, 18, 32],          // --brand-navy   (masthead band, logo navy)
  brand: [255, 107, 25],       // --brand-orange (brand rule, section markers)
  strong: [11, 27, 46],        // --text-strong
  body: [34, 52, 72],          // --text-body
  muted: [86, 101, 119],       // --text-muted
  subtle: [124, 138, 153],     // --text-subtle
  raised: [241, 244, 249],     // --surface-raised
  border: [217, 221, 225],     // --border-default, flattened
  hairline: [233, 236, 240],   // --border-subtle, flattened
  ink0: [245, 249, 253],       // --ink-0 (text on navy)
  ink2: [156, 178, 201],       // --ink-2 (secondary text on navy)
  // Semantic status: edge colour = *-600, fill = *-soft on white
  critical: [226, 59, 65], criticalSoft: [251, 231, 232],   // Alert · Down
  warning: [217, 138, 0], warningSoft: [250, 239, 219],     // Idle · Due soon
  info: [14, 111, 224], infoSoft: [232, 243, 255],          // Info (azure)
  success: [31, 168, 98], successSoft: [228, 245, 236]      // Healthy
}

// Alert box colour follows the alert's severity, using the foundations' status meanings.
const severityTone = (severity) => {
  const s = String(severity || '').toUpperCase()
  if (s === 'CRITICAL' || s === 'EMERGENCY') return { edge: C.critical, bg: C.criticalSoft }
  if (s === 'HIGH' || s === 'MEDIUM') return { edge: C.warning, bg: C.warningSoft }
  if (s === 'LOW') return { edge: C.info, bg: C.infoSoft }
  return { edge: C.subtle, bg: C.raised } // unknown severity: neutral, never a guessed tone
}

export const NOT_RECORDED = 'Not recorded'
const MAX_IMAGE_PX = 1600
const JPEG_QUALITY = 0.85

const clean = (v) => (v === null || v === undefined ? '' : String(v)).trim()
const orNotRecorded = (v) => clean(v) || NOT_RECORDED

// Alert descriptions are HTML. Keep paragraph / line / list structure as newlines
// before stripping tags so the PDF text reads the same as the drawer.
export const htmlToPlainText = (html) => {
  if (!html) return ''
  const withBreaks = String(html)
    .replace(/<!--[\s\S]*?-->/g, '')
    // Source whitespace is not significant in HTML; generated alert bodies are
    // indented templates, so collapse it before turning tags into line breaks.
    .replace(/\s+/g, ' ')
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\s*\/\s*(p|div|h[1-6]|tr|ul|ol)\s*>/gi, '\n')
    .replace(/<\s*(p|div|h[1-6]|ul|ol)(\s[^>]*)?>/gi, '\n')
    .replace(/<\s*li(\s[^>]*)?>/gi, '\n• ')
  const tmp = document.createElement('div')
  tmp.innerHTML = withBreaks
  return (tmp.textContent || tmp.innerText || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .join('\n')
}

// Alert body HTML -> blocks the PDF can lay out with the same structure the drawer
// shows: headings, paragraphs, bullet / numbered lists, line breaks, bold / italic
// and links. Parsed with DOMParser (inert: no scripts run, no images load).
// Only http(s) links become clickable; any other href (javascript:, mailto:…) is
// printed as plain text, never dropped.
// Block: { type: 'p'|'h'|'li'|'br', marker?, indent?, runs: [{ text, bold, italic, href }] }
//     or { type: 'table', runs: [], head: [[cell]], body: [[cell]] }, cell = { text, colSpan?, th? }
const BLOCK_TAGS = new Set(['P', 'DIV', 'SECTION', 'ARTICLE', 'BLOCKQUOTE', 'TABLE', 'TBODY', 'THEAD', 'TR', 'PRE'])

// Text of one table cell. Cells print as plain text: line breaks are kept, bold /
// italic / links inside a cell are not. A table nested in a cell is flattened here.
const cellText = (cell) => {
  let out = ''
  const visit = (n) => {
    if (n.nodeType === 3) { out += n.nodeValue.replace(/\s+/g, ' '); return }
    if (n.nodeType !== 1 || n.tagName === 'SCRIPT' || n.tagName === 'STYLE') return
    if (n.tagName === 'BR') { out += '\n'; return }
    const block = BLOCK_TAGS.has(n.tagName) || n.tagName === 'LI' || /^H[1-6]$/.test(n.tagName)
    if (block) out += '\n'
    Array.from(n.childNodes).forEach(visit)
    if (block) out += '\n'
    else if (n.tagName === 'TD' || n.tagName === 'TH') out += ' '
  }
  Array.from(cell.childNodes).forEach(visit)
  return out.split('\n').map((l) => l.trim()).filter(Boolean).join('\n')
}

// <table> -> table block with a rectangular grid, so every row has the same column
// count. colspan is kept. A rowspan cell prints in its first row and leaves blank
// cells below it, so the rows stay independent and can split across pages.
// Header rows are the leading <thead> rows, or a first row made only of <th>.
const tableToBlock = (table) => {
  const pending = [] // per column: rows still covered by a rowspan from above
  const rows = Array.from(table.rows).map((tr) => {
    const cells = []
    let col = 0
    const fillSpanned = () => { while (pending[col] > 0) { pending[col]--; cells.push({ text: '' }); col++ } }
    Array.from(tr.cells).forEach((cell) => {
      fillSpanned()
      const colSpan = Math.min(Math.max(1, cell.colSpan || 1), 50)
      const rowSpan = Math.max(1, cell.rowSpan || 1)
      cells.push({ text: cellText(cell), colSpan, th: cell.tagName === 'TH' })
      for (let k = 0; k < colSpan; k++) pending[col + k] = rowSpan - 1
      col += colSpan
    })
    fillSpanned()
    return { cells, cols: col, inHead: tr.parentNode.tagName === 'THEAD' }
  }).filter((r) => r.cells.some((c) => c.text))
  if (!rows.length) return null
  const cols = Math.max(...rows.map((r) => r.cols))
  rows.forEach((r) => { for (let k = r.cols; k < cols; k++) r.cells.push({ text: '' }) })
  let headCount = 0
  while (headCount < rows.length && rows[headCount].inHead) headCount++
  if (!headCount && rows[0].cells.every((c) => c.th)) headCount = 1
  if (headCount === rows.length) headCount = 0 // header only: print it as ordinary rows
  return {
    type: 'table',
    runs: [],
    head: rows.slice(0, headCount).map((r) => r.cells),
    body: rows.slice(headCount).map((r) => r.cells)
  }
}

export const htmlToBlocks = (html) => {
  if (!html) return []
  const root = new DOMParser().parseFromString(String(html), 'text/html').body
  const blocks = []
  const lists = []
  let current = null
  const start = (type, extra) => { current = { type, runs: [], ...extra }; blocks.push(current) }
  const walk = (node, style) => {
    if (node.nodeType === 3) {
      const text = node.nodeValue.replace(/\s+/g, ' ')
      if (!text.trim() && !current) return
      if (!current) start('p')
      current.runs.push({ text, bold: style.bold, italic: style.italic, href: style.href })
      return
    }
    if (node.nodeType !== 1) return // comments etc.
    const tag = node.tagName
    if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'HEAD') return
    const children = (s) => Array.from(node.childNodes).forEach((c) => walk(c, s))
    if (tag === 'BR') {
      start('br', { indent: current ? current.indent : 0 })
      return
    }
    const next = { ...style }
    if (tag === 'STRONG' || tag === 'B') next.bold = true
    if (tag === 'EM' || tag === 'I') next.italic = true
    if (tag === 'A') {
      const href = String(node.getAttribute('href') || '').trim()
      if (/^https?:\/\//i.test(href)) next.href = href
    }
    if (tag === 'UL' || tag === 'OL') {
      lists.push({ ordered: tag === 'OL', n: 0 })
      current = null; children(next); current = null
      lists.pop()
      return
    }
    if (tag === 'LI') {
      const list = lists[lists.length - 1]
      const marker = list && list.ordered ? `${++list.n}.` : '•'
      start('li', { marker, indent: 14 * Math.max(1, lists.length) })
      children(next); current = null
      return
    }
    if (/^H[1-6]$/.test(tag)) {
      start('h'); children({ ...next, bold: true }); current = null
      return
    }
    if (tag === 'TABLE') {
      current = null
      if (node.caption) { walk(node.caption, next); current = null }
      const table = tableToBlock(node)
      if (table) blocks.push(table)
      return
    }
    if (BLOCK_TAGS.has(tag)) {
      current = null; children(next); current = null
      return
    }
    children(next)
  }
  Array.from(root.childNodes).forEach((c) => walk(c, {}))

  // Trim whitespace at block edges and collapse doubled spaces across run boundaries.
  return blocks
    .map((b) => {
      const runs = []
      b.runs.forEach((r) => {
        let text = r.text
        const prev = runs[runs.length - 1]
        if ((!prev || /\s$/.test(prev.text)) && /^\s/.test(text)) text = text.replace(/^\s+/, '')
        if (text) runs.push({ ...r, text })
      })
      while (runs.length && !runs[runs.length - 1].text.trim()) runs.pop()
      if (runs.length) runs[runs.length - 1].text = runs[runs.length - 1].text.replace(/\s+$/, '')
      return { ...b, runs }
    })
    .filter((b) => b.type === 'table' || b.runs.some((r) => r.text.trim()))
}

// Plain text (analyst entries) -> blocks. Each line is kept; a blank line becomes
// paragraph spacing.
const textToBlocks = (text, style = {}) => {
  const blocks = []
  let gap = false
  String(text || '').split(/\r?\n/).forEach((line) => {
    if (!line.trim()) { gap = blocks.length > 0; return }
    blocks.push({ type: blocks.length === 0 || gap ? 'p' : 'br', runs: [{ text: line, ...style }] })
    gap = false
  })
  return blocks
}

// Saved Analysis / Action Suggested / Action Taken for an alert. Mirrors the
// resolution in AlertDrawer: the most recent statusHistory entry carrying a
// populated analysisId is the source of truth; top-level fields are used only
// when that entry has no value for the field.
export const resolveSavedAnalysis = (alert) => {
  const result = {
    analysis: clean(alert && alert.analysis),
    actionSuggested: clean(alert && alert.actionSuggested),
    actionTaken: clean(alert && alert.actionTaken)
  }
  const history = Array.isArray(alert && alert.statusHistory) ? alert.statusHistory : []
  const time = (h) => {
    const d = h && (h.timestamp || h.date)
    return d ? moment(d).valueOf() : 0
  }
  const latest = [...history]
    .sort((a, b) => time(b) - time(a))
    .find((h) => h && h.analysisId && typeof h.analysisId === 'object')
  if (latest) {
    const a = latest.analysisId
    if (clean(a.analysis)) result.analysis = clean(a.analysis)
    if (clean(a.actionSuggested)) result.actionSuggested = clean(a.actionSuggested)
    if (clean(a.actionTaken)) result.actionTaken = clean(a.actionTaken)
  }
  return result
}

const isImageAttachment = (f) =>
  Boolean(f && (f.isImage || (f.mimeType && String(f.mimeType).startsWith('image/'))))

// Fetch an attachment and re-encode it as a size-capped JPEG data URL (jsPDF 1.5.3
// handles JPEG reliably; re-encoding also normalises PNG/WebP/HEIC-in-browser).
// Rejects with a human-readable reason; never resolves with a placeholder.
const loadImageAsJpeg = async (url) => {
  if (!/^https?:\/\//i.test(url || '')) throw new Error('no downloadable file URL')
  let response
  try {
    // cache: 'no-store' is required. The drawer already shows these S3 images via
    // plain <img> tags, and S3 serves them without `Vary: Origin`, so the browser's
    // cached copy has no CORS header and a cached CORS fetch fails. A fresh request
    // gets the bucket's CORS headers (verified on production, AL-00747, 2026-09-16).
    response = await fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-store' })
  } catch (e) {
    throw new Error('file server blocked the download (network/CORS)')
  }
  if (!response.ok) throw new Error(`file server returned HTTP ${response.status}`)
  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('file is not a readable image'))
      el.src = objectUrl
    })
    const scale = Math.min(1, MAX_IMAGE_PX / Math.max(img.naturalWidth, img.naturalHeight))
    const w = Math.max(1, Math.round(img.naturalWidth * scale))
    const h = Math.max(1, Math.round(img.naturalHeight * scale))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#ffffff' // transparent PNG areas would turn black in JPEG
    ctx.fillRect(0, 0, w, h)
    ctx.drawImage(img, 0, 0, w, h)
    return { dataUrl: canvas.toDataURL('image/jpeg', JPEG_QUALITY), w, h }
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

const joinParts = (parts, sep) => parts.map(clean).filter(Boolean).join(sep)

const safeFileName = (s) => String(s || '').replace(/[^A-Za-z0-9-]+/g, '_').replace(/^_+|_+$/g, '')

/**
 * Build and download the PDF.
 *
 * @param {object} params
 * @param {object} params.alert        mapped alert object from Alert/index.js
 * @param {string} params.organisation organisation name ('' if unknown)
 * @param {Array}  params.attachments  saved attachments: { name, url, mimeType, isImage }
 * @returns {Promise<{ fileName, pages, embedded, failed: [{name, reason}], nonImage: [name], unprintable }>}
 *   unprintable = number of characters printed as [?] (see util/pdfText.js)
 */
export const buildAlertReportPdf = async ({ alert, organisation, attachments }) => {
  if (!alert) throw new Error('No alert selected')

  // Every drawn string goes through pdfText (see util/pdfText.js), which counts the [?] marks.
  const pdf = createPdfTextTracker()
  const pdfText = pdf.clean

  const saved = resolveSavedAnalysis(alert)
  const serial = clean(alert.id) || NOT_RECORDED
  const typeLabel = formatAlertTypeLabel(alert.type) || clean(alert.type) || 'Alert'
  const titleCase = (s) => s.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase()).replace(/\bIot\b/g, 'IoT')

  // ---- evidence: load every image first so failures are known before layout ----
  const files = (Array.isArray(attachments) ? attachments : []).filter((f) => f && f.status !== 'uploading')
  const imageFiles = files.filter(isImageAttachment)
  const nonImage = files.filter((f) => !isImageAttachment(f)).map((f) => clean(f.name) || 'Unnamed file')
  const loaded = await Promise.all(imageFiles.map(async (f) => {
    const name = clean(f.name) || 'Unnamed image'
    try {
      return { name, ...(await loadImageAsJpeg(f.url)) }
    } catch (e) {
      console.error(`Alert PDF: attachment "${name}" not embedded (${f.url}):`, e)
      return { name, error: e.message }
    }
  }))
  const images = loaded.filter((x) => !x.error)
  const failed = loaded.filter((x) => x.error).map((x) => ({ name: x.name, reason: x.error }))

  // ---- document ----
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  doc.setFont('helvetica')
  const W = doc.internal.pageSize.getWidth()
  const H = doc.internal.pageSize.getHeight()
  const M = 40                 // --space-8
  const CW = W - M * 2
  const MAST_H = 96            // page-1 navy masthead
  const BAND_H = 44            // navy band on continuation pages
  const RULE = 3               // brand orange rule under the band
  const BOTTOM = H - 52        // above the footer
  const LINE = 14

  const fill = (c) => doc.setFillColor(c[0], c[1], c[2])
  const ink = (c) => doc.setTextColor(c[0], c[1], c[2])
  const draw = (c) => doc.setDrawColor(c[0], c[1], c[2])
  const font = (size, style = 'normal') => { doc.setFontSize(size); doc.setFontType(style) }
  // The wordmark is decoration: if it ever fails to decode, the report must still generate.
  const drawLogo = (x, yTop, w) => {
    try { doc.addImage(LOGO_ON_NAVY, 'JPEG', x, yTop, w, w * (LOGO_H / LOGO_W)) } catch (e) { /* optional */ }
  }

  const reportTitle = pdfText(`${titleCase(typeLabel)} Report`)
  const band = (height) => {
    fill(C.navy); doc.rect(0, 0, W, height, 'F')
    fill(C.brand); doc.rect(0, height, W, RULE, 'F')
  }

  let y = 0
  const newPage = () => {
    doc.addPage()
    band(BAND_H)
    ink(C.ink0); font(11, 'bold')
    doc.text(reportTitle, M, 27)
    drawLogo(W - M - 84, 15, 84)
    y = BAND_H + RULE + 24
  }
  const ensure = (h) => { if (y + h > BOTTOM) newPage() }

  // Section marker: short brand-orange bar + strong heading (foundations: brand orange
  // for brand expression / eyebrows; azure and status colours are kept for meaning).
  const sectionHeading = (text) => {
    ensure(80) // heading + at least the first boxed line, so a heading is never orphaned
    y += 12
    fill(C.brand); doc.rect(M, y - 10, 3, 13, 'F')
    ink(C.strong); font(12.5, 'bold')
    doc.text(text, M + 10, y + 1)
    y += 14
  }

  // ---- rich text: blocks -> wrapped lines -> drawn (flows across pages) ----
  const BODY_PT = 10.5
  const HEAD_PT = 11.5
  const styleName = (r) => (r.bold && r.italic ? 'bolditalic' : r.bold ? 'bold' : r.italic ? 'italic' : 'normal')
  const measure = (text, run, size) => { font(size, styleName(run)); return doc.getStringUnitWidth(text) * size }
  // Space above each block, so paragraphs / headings / lists read like the drawer.
  const gapBefore = (b, prev) => {
    if (!prev) return 0
    if (b.type === 'table' || prev.type === 'table') return 8
    if (b.type === 'br') return 0
    if (b.type === 'h') return 9
    if (b.type === 'li') return prev.type === 'li' || prev.type === 'br' ? 3 : 6
    return 7
  }

  // ---- tables inside rich text ----
  // jspdf-autotable draws the grid. The table is measured first (column widths, row
  // heights), so its rows paginate like text lines and the tinted box can be drawn
  // behind them. Each page's rows are then drawn with the measured widths fixed, and
  // the header row is repeated when a table continues on a new page.
  const TABLE_OPTIONS = {
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: 8, cellPadding: 4, textColor: C.strong, lineColor: C.border, lineWidth: 0.5, fillColor: [255, 255, 255] },
    headStyles: { fillColor: C.raised, textColor: C.strong, fontStyle: 'bold' },
    rowPageBreak: 'avoid'
  }
  const tableRows = (rows, widths) => rows.map((r) => {
    let col = 0
    return r.map((c) => {
      const colSpan = c.colSpan || 1
      const styles = {}
      if (c.th) styles.fontStyle = 'bold'
      if (widths && colSpan === 1) styles.cellWidth = widths[col]
      col += colSpan
      return { content: c.text, colSpan, styles }
    })
  })

  // Table block -> one line per body row. Line: { gap, h, table, row }. Row 0 carries
  // the header's height, so the header is never left alone at the foot of a page.
  const layoutTable = (b, width, gap) => {
    const printable = (rows) => rows.map((r) => r.map((c) => ({ ...c, text: pdfText(c.text) })))
    const t = { head: printable(b.head), body: printable(b.body) }
    const measured = __createTable(doc, { ...TABLE_OPTIONS, tableWidth: width, head: tableRows(t.head), body: tableRows(t.body) })
    t.widths = measured.columns.map((c) => c.width)
    t.headH = measured.head.reduce((sum, r) => sum + r.height, 0)
    return measured.body.map((r, i) => ({ gap: i ? 0 : gap, h: r.height + (i ? 0 : gap + t.headH), table: t, row: i }))
  }

  const drawTableRows = (t, from, to, x, top) => autoTable(doc, {
    ...TABLE_OPTIONS,
    startY: top,
    margin: { left: x, top: 0, bottom: 0 },
    tableWidth: t.widths.reduce((sum, w) => sum + w, 0),
    columnStyles: t.widths.reduce((o, w, i) => { o[i] = { cellWidth: w }; return o }, {}),
    head: tableRows(t.head, t.widths),
    body: tableRows(t.body.slice(from, to + 1), t.widths)
  })

  // Word-wrap blocks into lines. Line: { gap, h, size, runs: [{ text, x, w, bold, italic, href, strong }] }
  const layoutBlocks = (rawBlocks, width) => {
    // Printable text first, so measuring and drawing use the same string. A block that
    // held only emoji has nothing left to print and is skipped.
    const blocks = rawBlocks
      .map((b) => ({ ...b, runs: b.runs.map((r) => ({ ...r, text: pdfText(r.text) })) }))
      .filter((b) => b.type === 'table' || b.runs.some((r) => r.text.trim()))
    const lines = []
    blocks.forEach((b, bi) => {
      if (b.type === 'table') {
        layoutTable(b, width, gapBefore(b, blocks[bi - 1])).forEach((l) => lines.push(l))
        return
      }
      const size = b.type === 'h' ? HEAD_PT : BODY_PT
      const lineH = b.type === 'h' ? 16 : LINE
      const indent = b.indent || 0
      const strong = b.type === 'h' || b.type === 'lead'
      let line = { gap: gapBefore(b, blocks[bi - 1]), h: 0, size, runs: [] }
      let x = indent
      if (b.type === 'li') line.runs.push({ text: b.marker, x: indent - 11, w: 0 })
      const pushLine = () => { line.h = line.gap + lineH; lines.push(line); line = { gap: 0, h: 0, size, runs: [] }; x = indent }
      const tokens = []
      b.runs.forEach((r) => r.text.split(/(\s+)/).forEach((t) => { if (t) tokens.push({ ...r, text: t }) }))
      tokens.forEach((tok) => {
        const isSpace = !tok.text.trim()
        if (isSpace) {
          if (x === indent) return
          const w = measure(' ', tok, size)
          if (x + w <= width) { line.runs.push({ ...tok, text: ' ', x, w, strong }); x += w }
          return
        }
        let text = tok.text
        let w = measure(text, tok, size)
        if (x + w > width && x > indent) pushLine()
        // A single word wider than the line (long IDs / URLs) is broken by character.
        while (w > width - indent && text.length > 1) {
          let k = text.length - 1
          while (k > 1 && measure(text.slice(0, k), tok, size) > width - indent) k--
          line.runs.push({ ...tok, text: text.slice(0, k), x, w: measure(text.slice(0, k), tok, size), strong })
          pushLine()
          text = text.slice(k)
          w = measure(text, tok, size)
        }
        line.runs.push({ ...tok, text, x, w, strong })
        x += w
      })
      pushLine()
    })
    return lines
  }

  const drawRun = (run, x0, base, size, box) => {
    if (!run.text) return
    font(size, styleName(run))
    ink(run.href ? C.info : run.strong ? C.strong : (box && box.textColor) || C.body)
    doc.text(run.text, x0 + run.x, base)
    if (run.href && run.w) {
      draw(C.info); doc.setLineWidth(0.5)
      doc.line(x0 + run.x, base + 1.8, x0 + run.x + run.w, base + 1.8)
    }
  }

  // One clickable area per link per line (a link's words are separate runs).
  const addLineLinks = (runs, x0, base, size) => {
    let span = null
    const flush = () => { if (span) doc.link(x0 + span.x, base - size * 0.85, span.w, size * 1.15, { url: span.href }); span = null }
    runs.forEach((run) => {
      if (run.href && span && span.href === run.href) span.w = run.x + run.w - span.x
      else { flush(); if (run.href) span = { href: run.href, x: run.x, w: run.w } }
    })
    flush()
  }

  // Draw laid-out lines, optionally inside a tinted box with a 3pt status edge.
  // The box is closed at a page break and reopened on the next page.
  const richParagraph = (blocks, box) => {
    const pad = box ? 12 : 0
    const textX = M + pad + (box ? 3 : 0)
    const lines = layoutBlocks(blocks, CW - pad * 2 - (box ? 3 : 0))
    // A table continuing at the top of a page gets its header row again.
    const lineH = (k, first) =>
      lines[k].h + (first && lines[k].table && lines[k].row > 0 ? lines[k].table.headH : 0)
    let i = 0
    while (i < lines.length) {
      ensure(lineH(i, true) + pad * 2)
      let h = pad * 2
      let j = i
      while (j < lines.length && (j === i || y + h + lines[j].h <= BOTTOM)) { h += lineH(j, j === i); j++ }
      if (box) {
        fill(box.bg); draw(C.hairline); doc.setLineWidth(0.6)
        doc.rect(M, y, CW, h, 'FD')
        fill(box.edge); doc.rect(M, y, 3, h, 'F')
      }
      let top = y + pad
      for (let k = i; k < j; k++) {
        const ln = lines[k]
        if (ln.table) {
          // Draw this page's run of rows from the same table in one go.
          let end = k
          let rowsH = lineH(k, k === i)
          while (end + 1 < j && lines[end + 1].table === ln.table) { end++; rowsH += lines[end].h }
          drawTableRows(ln.table, ln.row, lines[end].row, textX, top + ln.gap)
          top += rowsH
          k = end
          continue
        }
        const base = top + ln.h - 4 // baseline sits 4pt above the line's bottom
        for (let r = 0; r < ln.runs.length; r++) drawRun(ln.runs[r], textX, base, ln.size, box)
        addLineLinks(ln.runs, textX, base, ln.size)
        top += ln.h
      }
      y += h
      i = j
      if (i < lines.length) newPage()
    }
    y += 8
  }

  // Plain analyst text (line breaks kept) in the same layout engine.
  const paragraph = (text, box) =>
    richParagraph(textToBlocks(text, { italic: Boolean(box && box.italic) }), box)

  const notRecordedBox = { bg: C.raised, edge: C.subtle, textColor: C.muted, italic: true }

  // ================= page 1: masthead =================
  band(MAST_H)
  ink(C.brand); font(8, 'bold')
  doc.text('AIM UPDATE', M, 30)
  ink(C.ink0); font(20, 'bold')
  doc.text(doc.splitTextToSize(reportTitle, CW - 200)[0], M, 54)
  ink(C.ink2); font(9)
  doc.text(`Report Generated ${moment().format('DD MMM YYYY, HH:mm')}`, M, 74)
  // Logo top-right with the organisation the report is for directly beneath it.
  drawLogo(W - M - 132, 24, 132)
  const orgName = pdfText(clean(organisation) === 'N/A' ? '' : clean(organisation))
  ink(orgName ? C.ink0 : C.ink2); font(10, orgName ? 'bold' : 'italic')
  doc.splitTextToSize(orgName || `Organisation ${NOT_RECORDED.toLowerCase()}`, 190)
    .forEach((l, i) => doc.text(l, W - M, 66 + i * 12, { align: 'right' }))
  y = MAST_H + RULE + 20

  // ---- details ----
  const assetIdentity = joinParts([alert.manpowerName, alert.assetCode, alert.altName, alert.ownerCode], ' / ')
  const model = joinParts([alert.assetType, alert.assetSubType, alert.assetModel], ' - ')
  const location = joinParts([alert.location, alert.subLocation], ' / ')
  const alertDate = alert.alertDate || alert.createdAt
  const tone = severityTone(alert.severity)

  autoTable(doc, {
    startY: y,
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: 9.5, cellPadding: 7, textColor: C.strong, lineColor: C.border, lineWidth: 0.5, fillColor: [255, 255, 255] },
    columnStyles: {
      0: { cellWidth: 84, textColor: C.muted, fillColor: C.raised },
      1: { fontStyle: 'bold' },
      2: { cellWidth: 78, textColor: C.muted, fillColor: C.raised },
      3: { fontStyle: 'bold' }
    },
    body: [
      ['Alert Date', alertDate ? moment(alertDate).format('DD MMM YYYY, HH:mm') : NOT_RECORDED, 'Alert No.', serial],
      [alert.manpowerName && !alert.assetCode ? 'Manpower' : 'Asset Code', orNotRecorded(assetIdentity), 'Model', orNotRecorded(model)],
      ['Location', orNotRecorded(location), 'Severity', orNotRecorded(alert.severity)]
    ].map((row) => row.map(pdfText)),
    didParseCell: (d) => {
      if (d.section !== 'body') return
      // Alert number is the reference the client quotes back, so it is the most prominent value.
      if (d.row.index === 0 && d.column.index === 3 && serial !== NOT_RECORDED) {
        d.cell.styles.fontSize = 12
        d.cell.styles.textColor = C.navy
      }
      // Severity carries its status colour, matching the Alert box below.
      if (d.row.index === 2 && d.column.index === 3 && clean(alert.severity)) {
        d.cell.styles.textColor = tone.edge
      }
    },
    margin: { left: M, right: M }
  })
  y = doc.lastAutoTable.finalY + 6

  sectionHeading('Alert')
  const alertTitle = htmlToPlainText(alert.title)
  const alertBody = htmlToPlainText(alert.body)
  if (!alertTitle && !alertBody) paragraph(NOT_RECORDED, notRecordedBox)
  else {
    // Title in bold, then the body with its HTML structure (spacing, lists, bold, links).
    const lead = alertTitle ? [{ type: 'lead', runs: [{ text: alertTitle, bold: true }] }] : []
    richParagraph(lead.concat(alertBody !== alertTitle ? htmlToBlocks(alert.body) : []), tone)
  }

  sectionHeading('Analysis')
  if (saved.analysis) paragraph(saved.analysis)
  else paragraph('Analysis has not been recorded for this alert.', notRecordedBox)

  sectionHeading('Suggested Action')
  if (saved.actionSuggested) paragraph(saved.actionSuggested, { bg: C.infoSoft, edge: C.info })
  else paragraph('A suggested action has not been recorded for this alert.', notRecordedBox)

  sectionHeading('Action Taken')
  if (saved.actionTaken) paragraph(saved.actionTaken, { bg: C.successSoft, edge: C.success })
  else paragraph('Action taken has not been recorded yet.', notRecordedBox)

  // ================= evidence =================
  if (images.length || failed.length || nonImage.length) {
    newPage()
    ink(C.strong); font(16, 'bold')
    doc.text('Supporting Evidence', M, y + 4)
    ink(C.muted); font(9)
    doc.text('Photographs and attachments submitted with this alert.', M, y + 20)
    y += 40

    const CAPTION_H = 22
    const GAP = 16             // --space-4
    const HALF_W = (CW - GAP) / 2
    const MAX_H = 300
    const box = (img, maxW) => {
      const s = Math.min(maxW / img.w, MAX_H / img.h)
      return { w: img.w * s, h: img.h * s }
    }
    const photo = (img, x, top, b) => {
      doc.addImage(img.dataUrl, 'JPEG', x, top, b.w, b.h)
      draw(C.border); doc.setLineWidth(0.5); doc.rect(x, top, b.w, b.h, 'S') // hairline frame
    }
    const caption = (text, cx, top) => {
      ink(C.muted); font(8.5)
      const l = doc.splitTextToSize(pdfText(text), HALF_W)[0]
      doc.text(l, cx, top + 13, { align: 'center' })
    }

    // Portrait photos (receipts, dispenser displays) first, two to a row; then
    // landscape ones full width. Upload order is kept within each group.
    const ordered = images.filter((im) => im.h >= im.w).concat(images.filter((im) => im.h < im.w))
    let i = 0
    while (i < ordered.length) {
      const a = ordered[i]
      const b = ordered[i + 1]
      const aPortrait = a.h >= a.w
      if (aPortrait && b && b.h >= b.w) {
        const ba = box(a, HALF_W)
        const bb = box(b, HALF_W)
        const rowH = Math.max(ba.h, bb.h)
        ensure(rowH + CAPTION_H)
        const cxA = M + HALF_W / 2
        const cxB = M + HALF_W + GAP + HALF_W / 2
        photo(a, cxA - ba.w / 2, y, ba)
        photo(b, cxB - bb.w / 2, y, bb)
        caption(a.name, cxA, y + rowH)
        caption(b.name, cxB, y + rowH)
        y += rowH + CAPTION_H + GAP
        i += 2
      } else {
        const ba = box(a, aPortrait ? HALF_W : CW)
        ensure(ba.h + CAPTION_H)
        photo(a, W / 2 - ba.w / 2, y, ba)
        caption(a.name, W / 2, y + ba.h)
        y += ba.h + CAPTION_H + GAP
        i += 1
      }
    }

    if (failed.length) {
      sectionHeading('Attachments not embedded')
      paragraph(
        'The following image attachments exist on the alert but could not be included in this PDF:\n' +
        failed.map((f) => `• ${f.name} — ${f.reason}`).join('\n'),
        { bg: C.criticalSoft, edge: C.critical }
      )
    }
    if (nonImage.length) {
      sectionHeading('Other attachments')
      paragraph('These files are attached to the alert in AssetPro and are not reproduced in this PDF:\n' +
        nonImage.map((n) => `• ${n}`).join('\n'))
    }
  }

  // Last, so it covers every [?] above. Says where the original text is.
  const unprintable = pdf.count()
  if (unprintable > 0) {
    sectionHeading('Characters not printed')
    paragraph(unprintableNote(unprintable), { bg: C.warningSoft, edge: C.warning })
  }

  // ---- footer on every page ----
  const pages = doc.internal.getNumberOfPages()
  const footerSerial = toPdfText(serial).text // already counted in the details table
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p)
    draw(C.border); doc.setLineWidth(0.5)
    doc.line(M, H - 36, W - M, H - 36)
    ink(C.subtle); font(7.5)
    doc.text(`AssetPro  ·  AIM Update  ·  ${footerSerial}`, M, H - 23)
    doc.text(`Page ${p} of ${pages}`, W - M, H - 23, { align: 'right' })
  }

  const fileName = `AIM_${safeFileName(titleCase(typeLabel))}_Report_${safeFileName(serial)}.pdf`
  doc.save(fileName)
  return { fileName, pages, embedded: images.length, failed, nonImage, unprintable }
}
